import React, { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    category: '',
    message: ''
  });

  const [monthlyCount, setMonthlyCount] = useState(0);
  const [dailyCount, setDailyCount] = useState(0);
  const [isMonthlyLimitReached, setIsMonthlyLimitReached] = useState(false);
  const [isDailyLimitReached, setIsDailyLimitReached] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MONTHLY_LIMIT = 100;
  const DAILY_LIMIT = 6;

  // EmailJS 설정
  const EMAILJS_SERVICE_ID = 'service_rwl8udt__emailJS';
  const EMAILJS_TEMPLATE_ID = 'template_efqti19';
  const EMAILJS_USER_ID = 'sdgr0n4hHCDGHFXe4';

  useEffect(() => {
    // 월별 카운터 확인
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();
    
    // 월별 카운터
    const monthlyStorageKey = `contact_submissions_${currentYear}_${currentMonth}`;
    const savedMonthlyCount = localStorage.getItem(monthlyStorageKey);
    const monthlyCount = savedMonthlyCount ? parseInt(savedMonthlyCount) : 0;
    
    // 일별 카운터
    const dailyStorageKey = `contact_submissions_daily_${currentYear}_${currentMonth}_${currentDay}`;
    const savedDailyCount = localStorage.getItem(dailyStorageKey);
    const dailyCount = savedDailyCount ? parseInt(savedDailyCount) : 0;
    
    setMonthlyCount(monthlyCount);
    setDailyCount(dailyCount);
    setIsMonthlyLimitReached(monthlyCount >= MONTHLY_LIMIT);
    setIsDailyLimitReached(dailyCount >= DAILY_LIMIT);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 월 한도 체크
    if (isMonthlyLimitReached) {
      alert('이번달 상담신청이 초과되었습니다. 전화나 이메일로 직접 연락해 주세요.');
      return;
    }

    // 일일 한도 체크
    if (isDailyLimitReached) {
      alert('일일 상담신청이 초과되었습니다. 전화나 이메일로 직접 연락해 주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      // EmailJS 템플릿에 전송할 데이터
      const templateParams = {
        from_name: formData.name,
        from_phone: formData.phone,
        from_email: formData.email,
        category: formData.category,
        message: formData.message,
        to_email1: 'tax_gyeol@naver.com',
        to_email2: 'cta9321@kakao.com',
        submission_date: new Date().toLocaleString('ko-KR')
      };

      // EmailJS로 이메일 전송
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_USER_ID
      );

      console.log('이메일 전송 성공:', result.text);
      
      // 성공시 카운터 증가
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const currentDay = currentDate.getDate();
      
      // 월별 카운터 증가
      const monthlyStorageKey = `contact_submissions_${currentYear}_${currentMonth}`;
      const newMonthlyCount = monthlyCount + 1;
      localStorage.setItem(monthlyStorageKey, newMonthlyCount.toString());
      setMonthlyCount(newMonthlyCount);
      
      // 일별 카운터 증가
      const dailyStorageKey = `contact_submissions_daily_${currentYear}_${currentMonth}_${currentDay}`;
      const newDailyCount = dailyCount + 1;
      localStorage.setItem(dailyStorageKey, newDailyCount.toString());
      setDailyCount(newDailyCount);
      
      // 한도 체크
      if (newMonthlyCount >= MONTHLY_LIMIT) {
        setIsMonthlyLimitReached(true);
      }
      if (newDailyCount >= DAILY_LIMIT) {
        setIsDailyLimitReached(true);
      }

      // 성공 메시지
      alert('상담 문의가 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.');
      
      // 폼 리셋
      setFormData({
        name: '',
        phone: '',
        email: '',
        category: '',
        message: ''
      });

    } catch (error) {
      console.error('이메일 전송 실패:', error);
      alert('전송 중 오류가 발생했습니다. 전화나 이메일로 직접 연락해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="page-title">상담문의</h1>
          <h2 className="main-quote">
            세무 문제로 고민이시라면 언제든지 연락주세요.<br />
            세무사가 친절하고 정확하게 상담해드립니다.
          </h2>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="contact-content">
        <div className="container">
          <div className="contact-wrapper">
            {/* Contact Form */}
            <div className="contact-form-section">
              <h3>온라인 상담 신청</h3>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">성함 *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="성함을 입력해주세요"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">연락처 *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="연락 가능한 번호를 입력해주세요"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">이메일</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="이메일 주소를 입력해주세요"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">상담 분야 *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">상담 분야를 선택해주세요</option>
                    <option value="기장업무">기장업무</option>
                    <option value="재산세제">재산세제</option>
                    <option value="경정청구">경정청구/조세불복</option>
                    <option value="병의원세무">병의원 세무</option>
                    <option value="세무컨설팅">세무 컨설팅</option>
                    <option value="기타">기타</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">상담 내용 *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="궁금한 내용을 자세히 작성해주시면 더 정확한 상담을 받으실 수 있습니다."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isMonthlyLimitReached || isDailyLimitReached || isSubmitting}
                >
                  {isSubmitting ? '전송 중...' : 
                   isMonthlyLimitReached ? '월 한도 초과' : 
                   isDailyLimitReached ? '일일 한도 초과' : '상담 신청하기'}
                </button>
                
                
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-section">
              <h3>연락처 정보</h3>
              <div className="contact-info">
                <div className="info-item">
                  <div className="info-icon">📞</div>
                  <div className="info-content">
                    <h4>전화 상담</h4>
                    <p>02-6949-5003</p>
                    <small>평일 09:00 - 18:00</small>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">📧</div>
                  <div className="info-content">
                    <h4>이메일</h4>
                    <p>tax_gyeol@naver.com</p>
                    <small>24시간 접수 가능</small>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">📍</div>
                  <div className="info-content">
                    <h4>사무실 위치</h4>
                    <p>서울특별시 중랑구 망우로 177, 3층<br />(중화동, 삼성빌딩)</p>
                    <small>지하철 7호선 중화역 도보 5분</small>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">💬</div>
                  <div className="info-content">
                    <h4>카톡상담 시간</h4>
                    <p>평일: 11:00 - 16:00</p>
                    <small>토요일/일요일/공휴일: 휴무</small>
                  </div>
                </div>
              </div>

              <div className="consultation-types">
                <h4>상담 방법</h4>
                <ul>
                  <li>📞 전화 상담</li>
                  <li>🏢 방문 상담</li>
                  <li>💬 카카오톡 상담</li>
                  <li>📧 이메일 상담</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <h3>자주 묻는 질문</h3>
          <div className="faq-list">
            <div className="faq-item">
              <h4>Q. 상담 비용이 있나요?</h4>
              <p>A. 초기 상담은 무료로 진행됩니다. 구체적인 업무 의뢰 시 별도 비용이 발생합니다.</p>
            </div>
            <div className="faq-item">
              <h4>Q. 병의원 세무에 특화되어 있나요?</h4>
              <p>A. 네, 병의원 세무에 특화된 전문 서비스를 제공하며, 의료업계의 특수한 세무 환경을 깊이 이해하고 있습니다.</p>
            </div>
            <div className="faq-item">
              <h4>Q. 다른 업종이나 다른 세금은 어떤가요?</h4>
              <p>A. 병의원 뿐만 아니라 약국, 광업, 건설업, 대부업 등 특수업종과 온라인판매, 음식점 등 일반업종도 진행하고 있습니다. 나아가 사업장 위치도 고려해야 합니다. 상담을 통해 모두 검토받으시길 바랍니다.</p>
            </div>
            <div className="faq-item">
              <h4>Q. 얼마나 빨리 상담받을 수 있나요?</h4>
              <p>A. 상담 신청 후 영업일 기준 24시간 이내에 연락드리며, 긴급한 경우 당일 상담도 가능합니다.<br />(다만, 세무조사 또는 신고기간에는 기존 고객이 최우선입니다)</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
